import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type CreateProfileInput, profileService } from '@/api';
import type { ProfileType } from '@/types';

export function useOnboarding() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  // Step 1: Nome
  const [name, setName] = useState<string>('');
  // Step 2: Perfis
  const [selectedProfiles, setSelectedProfiles] = useState<ProfileType[]>([]);
  // Step 3: Cargo e indústria
  const [role, setRole] = useState<string>('');
  const [roleLabel, setRoleLabel] = useState<string>('');
  const [industry, setIndustry] = useState<string>('');
  const [industryLabel, setIndustryLabel] = useState<string>('');

  const toggleProfile = useCallback((profile: ProfileType) => {
    setSelectedProfiles((prev) =>
      prev.includes(profile) ? prev.filter((p) => p !== profile) : [...prev, profile],
    );
  }, []);

  const createProfileMutation = useMutation({
    mutationFn: async (data: CreateProfileInput) => {
      // Primeiro cria o perfil
      const profile = await profileService.createProfile(data);

      // Depois faz update com todos os dados adicionais
      const updatedProfile = await profileService.updateProfile(profile.id, {
        profileTypes: selectedProfiles,
        role: roleLabel || undefined,
        industry: industryLabel || undefined,
      });
      return updatedProfile;
    },
    onSuccess: (profile) => {
      // Set the profile directly in cache to avoid race condition with ProtectedRoute
      queryClient.setQueryData(['profile'], profile);
      navigate('/feed');
    },
  });

  const completeOnboarding = useCallback(() => {
    createProfileMutation.mutate({
      userName: name || 'Meu Perfil',
      description: roleLabel && industryLabel ? `${roleLabel} - ${industryLabel}` : undefined,
    });
  }, [createProfileMutation, name, roleLabel, industryLabel]);

  const nextStep = useCallback(() => {
    if (step === 1 && name.trim()) {
      setStep(2);
    } else if (step === 2 && selectedProfiles.length > 0) {
      setStep(3);
    } else if (step === 3) {
      completeOnboarding();
    }
  }, [step, name, selectedProfiles.length, completeOnboarding]);

  const prevStep = useCallback(() => {
    if (step > 1) {
      setStep(step - 1);
    }
  }, [step]);

  const skipStep3 = useCallback(() => {
    completeOnboarding();
  }, [completeOnboarding]);

  const handleRoleChange = useCallback((value: string, label: string) => {
    setRole(value);
    setRoleLabel(label);
  }, []);

  const handleIndustryChange = useCallback((value: string, label: string) => {
    setIndustry(value);
    setIndustryLabel(label);
  }, []);

  const canProceed = (() => {
    if (step === 1) return name.trim().length > 0;
    if (step === 2) return selectedProfiles.length > 0;
    return true;
  })();

  return {
    step,
    totalSteps: 3,
    // Step 1
    name,
    setName,
    // Step 2
    selectedProfiles,
    toggleProfile,
    // Step 3
    role,
    industry,
    setRole: handleRoleChange,
    setIndustry: handleIndustryChange,
    // Navigation
    nextStep,
    prevStep,
    skipStep3,
    isLoading: createProfileMutation.isPending,
    error: createProfileMutation.error,
    canProceed,
  };
}
